import { safeSet } from '../lib/storage.js';
import { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext.jsx';

const PlayerContext = createContext(null);
export const usePlayer = () => useContext(PlayerContext);

const API = import.meta.env.VITE_API_URL || '/api';

// Holds the single <audio> element and playback state above the tab layer, so music
// keeps playing when the user navigates away from the Music tab.
export function PlayerProvider({ children }) {
  const { token } = useAuth();
  const audioRef = useRef(null);
  const [tracks, setTracks] = useState([]);
  const [curIdx, setCurIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(0.8);
  // Mini-player shows only after the user has actually started playback at least once,
  // and stays hidden if they dismissed it (persisted across reloads).
  const [started, setStarted] = useState(false);
  const [closed, setClosed] = useState(() => localStorage.getItem('mini_closed') === '1');

  const headers = { Authorization: `Bearer ${token}` };
  const cur = tracks[curIdx] || null;

  // Load the user's tracks once we have a token.
  const loadTracks = useCallback(() => {
    if (!token || token === 'local') return;
    fetch(`${API}/music/tracks`, { headers }).then(r => r.json()).then(d => { if (Array.isArray(d)) setTracks(d); }).catch(() => {});
  }, [token]);
  useEffect(() => { loadTracks(); }, [loadTracks]);

  // When the current track changes, point the audio element at it.
  useEffect(() => {
    if (!audioRef.current || !cur) return;
    const src = cur.file_path?.startsWith('/') ? `${window.location.origin}${cur.file_path}` : cur.file_path;
    if (audioRef.current.src !== src) audioRef.current.src = src;
    audioRef.current.volume = volume;
    if (playing) audioRef.current.play().catch(() => {});
  }, [curIdx, cur]); // eslint-disable-line

  useEffect(() => { if (audioRef.current) audioRef.current.volume = volume; }, [volume]);

  const playTrack = (idx) => {
    setClosed(false); localStorage.removeItem('mini_closed');
    setStarted(true);
    setCurIdx(idx);
    setPlaying(true);
    // src is set by the effect; play after a tick
    setTimeout(() => { audioRef.current?.play().then(() => setPlaying(true)).catch(() => {}); }, 0);
  };
  const togglePlay = () => {
    if (!audioRef.current || !cur) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { setClosed(false); localStorage.removeItem('mini_closed'); setStarted(true); audioRef.current.play().then(() => setPlaying(true)).catch(() => {}); }
  };
  const next = () => { if (tracks.length) playTrack((curIdx + 1) % tracks.length); };
  const prev = () => { if (tracks.length) playTrack((curIdx - 1 + tracks.length) % tracks.length); };
  const seek = (pct) => { if (audioRef.current?.duration) audioRef.current.currentTime = audioRef.current.duration * pct; };
  const closeMini = () => { audioRef.current?.pause(); setPlaying(false); setClosed(true); safeSet('mini_closed', '1'); };

  const value = {
    audioRef, tracks, setTracks, curIdx, setCurIdx, cur,
    playing, setPlaying, progress, volume, setVolume, closed, started,
    loadTracks, playTrack, togglePlay, next, prev, seek, closeMini,
    duration: audioRef.current?.duration || 0,
    currentTime: audioRef.current?.currentTime || 0,
  };

  return (
    <PlayerContext.Provider value={value}>
      {/* The one and only audio element, mounted above the tabs so it never unmounts */}
      <audio ref={audioRef}
        onTimeUpdate={e => setProgress(e.target.currentTime / (e.target.duration || 1))}
        onEnded={next}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      {children}
    </PlayerContext.Provider>
  );
}
